import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { checkIfNewsSectionInFirstIndex } from "scmp-app/lib/section";
import type { sectionSearchResultListItemSection$key } from "scmp-app/queries/__generated__/sectionSearchResultListItemSection.graphql";

import { Container, SectionTitle, TitleContainer } from "./styles";

export type Props = {
  reference: sectionSearchResultListItemSection$key;
};

export const SectionSearchResultListItem: FunctionComponent<Props> = ({
  reference: reference_,
  ...props
}) => {
  const section = useFragment<sectionSearchResultListItemSection$key>(
    graphql`
      fragment sectionSearchResultListItemSection on Section {
        fullSectionPath {
          ...helpersCheckIfNewsSectionInFirstIndexSection
          name
        }
        entityId
        ...entityLink
      }
    `,
    reference_,
  );

  const { currentValue } = useSearchContext();

  const handleSectionClick = () => {
    // GA4 21.2.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      customized_parameters: {
        search_text: currentValue,
        section_id: section?.entityId,
      },
      subcategory: "result",
    } as SearchEvent);
  };

  const sanitizedSectionName =
    section?.fullSectionPath
      ?.filter(
        (section, index) => notEmpty(section) && !checkIfNewsSectionInFirstIndex(section, index),
      )
      ?.map(section => section?.name) ?? [];

  return (
    <Container {...props}>
      <EntityLink onClick={handleSectionClick} reference={section}>
        <TitleContainer>
          <SectionTitle>{sanitizedSectionName.join(" / ")}</SectionTitle>
        </TitleContainer>
      </EntityLink>
    </Container>
  );
};

SectionSearchResultListItem.displayName = "SectionSearchResultListItem";
